/**
 * メンバー設定の画面。
 *
 * 担当者は Firestore に持つ（`members` コレクション）。以前は「いま出ているタスクの
 * 担当者」を数えて出していたので、タスクがまだ 1 件も無い人を置けず、直しても残らなかった。
 *
 * 保存先が空のときだけ、いまのタスクの担当者から取り込めるようにする（最初の 1 回ぶん）。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business メンバー設定の画面。チームごとの担当者を Firestore に足す・直す・消す
 * @graph-connects useSettings [calls] 設定の読み書き
 * @graph-connects filterByTeam [calls] チームごとのタスクに絞る
 * @graph-connects listAssignees [calls] タスクの担当者を取り出す
 */
import { useState } from 'react';
import { useSettings } from '@/api/use-settings';
import type { SettingEntry } from '@/api/client';
import { filterByTeam, listAssignees, TEAM_IDS, type Task, type TeamId } from '@/tasks/task';

/**
 * チームの表示名。
 * @graph-connects none
 */
const TEAM_LABEL: Record<TeamId, { label: string; fullName: string }> = {
  sft: { label: 'SFT', fullName: 'ストラテジックファイナンスチーム' },
  cpt: { label: 'CPT', fullName: 'コーポレートサポートチーム' },
};

/** @graph-connects none */
const SMALL_BUTTON =
  'cursor-pointer rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business まだ登録していない担当者を、いまのタスクから拾う。最初の取り込みに使う
 * @graph-connects listAssignees [calls] チームごとの担当者を取り出す
 * @graph-connects filterByTeam [calls] チームごとのタスクに絞る
 */
export function missingAssignees(
  tasks: readonly Task[],
  team: TeamId,
  settings: readonly SettingEntry[],
): string[] {
  const stored = new Set(
    settings.filter((entry) => entry.team === team).map((entry) => entry.name),
  );
  return listAssignees(filterByTeam(tasks, team)).filter((name) => !stored.has(name));
}

/**
 * MembersSettings の props。
 */
export interface MembersSettingsProps {
  /** 担当しているタスクの数を数えるタスク */
  tasks: readonly Task[];
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business メンバー設定の画面。チームごとの担当者を足す・直す・消す。保存先は Firestore
 * @graph-connects useSettings [calls] メンバーの読み書き
 * @graph-connects missingAssignees [calls] 未登録の担当者を拾う
 */
export function MembersSettings({ tasks }: MembersSettingsProps) {
  const { settings, loading, error, add, update, remove } = useSettings('members');
  const [editingId, setEditingId] = useState('');
  const [draftName, setDraftName] = useState('');
  // カレンダー取り込みで、ログインしている人をこの担当に結びつけるメールアドレス
  const [draftEmail, setDraftEmail] = useState('');

  const save = (id: string) => {
    if (draftName.trim() === '') {
      return;
    }
    void update(id, { name: draftName.trim(), email: draftEmail.trim() });
    setEditingId('');
  };

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      <header className="mb-4">
        <h1 className="m-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
          メンバー設定
        </h1>
        <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">
          チームごとの担当者を管理する。メールアドレスを登録すると、カレンダー取り込みで本人の予定がその担当に入ります
        </p>
      </header>

      {error === '' ? null : (
        <p className="mb-3 max-w-2xl rounded border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-4 py-2 text-sm text-[var(--color-doing-text)]">
          {error}
        </p>
      )}

      {/* 読み込み中の知らせを出すのは、まだ出せる一覧が無いときだけ。書いたあとの
          読み直しでも出すと、一覧が 1 行に縮んでスクロール位置が先頭へ戻ってしまう */}
      {loading && settings.length === 0 ? (
        <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          読み込んでいます…
        </p>
      ) : (
        <div className="max-w-2xl space-y-8">
          {TEAM_IDS.map((team) => {
            const teamTasks = filterByTeam(tasks, team);
            const teamMembers = settings.filter((member) => member.team === team);
            const missing = missingAssignees(tasks, team, settings);

            return (
              <section key={team}>
                <h2 className="m-0 mb-1 text-sm font-semibold text-[var(--color-text-primary)]">
                  {TEAM_LABEL[team].label}
                </h2>
                <p className="mt-0 mb-3 text-xs text-[var(--color-text-muted)]">
                  {TEAM_LABEL[team].fullName}
                </p>

                <ul className="m-0 list-none p-0">
                  {teamMembers.map((member) => (
                    <li
                      key={member.id}
                      className="flex items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] py-2 last:border-b-0"
                    >
                      {editingId === member.id ? (
                        <span className="flex min-w-0 flex-1 gap-2">
                          <input
                            type="text"
                            aria-label="メンバー名"
                            value={draftName}
                            onChange={(event) => {
                              setDraftName(event.target.value);
                            }}
                            className="w-32 min-w-0 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm text-[var(--color-text-primary)]"
                          />
                          <input
                            type="email"
                            aria-label="メールアドレス"
                            placeholder="name@air-closet.com"
                            value={draftEmail}
                            onChange={(event) => {
                              setDraftEmail(event.target.value);
                            }}
                            className="min-w-0 flex-1 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm text-[var(--color-text-primary)]"
                          />
                        </span>
                      ) : (
                        <span className="min-w-0 flex-1 text-sm text-[var(--color-text-primary)]">
                          {member.name}
                          {/* 担当しているタスクの数。消していいかの判断材料になる */}
                          <span className="ml-2 text-xs tabular-nums text-[var(--color-text-muted)]">
                            {teamTasks.filter((task) => task.assignee === member.name).length}件
                          </span>
                          {/* カレンダー取り込みで本人を担当に結びつけるメールアドレス */}
                          <span className="ml-3 text-xs text-[var(--color-text-muted)]">
                            {member.email ?? 'メール未登録'}
                          </span>
                        </span>
                      )}

                      <span className="flex shrink-0 gap-1">
                        {editingId === member.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                save(member.id);
                              }}
                              className={SMALL_BUTTON}
                            >
                              保存
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId('');
                              }}
                              className={SMALL_BUTTON}
                            >
                              戻る
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              aria-label={`${member.name}を編集`}
                              onClick={() => {
                                setEditingId(member.id);
                                setDraftName(member.name);
                                setDraftEmail(member.email ?? '');
                              }}
                              className={SMALL_BUTTON}
                            >
                              編集
                            </button>
                            <button
                              type="button"
                              aria-label={`${member.name}を削除`}
                              onClick={() => {
                                void remove(member.id);
                              }}
                              className="cursor-pointer rounded border border-[var(--color-border-subtle)] px-2 py-0.5 text-xs text-[var(--color-overdue)]"
                            >
                              削除
                            </button>
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      void add({ team, name: '新しいメンバー' });
                    }}
                    className="cursor-pointer rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)]"
                  >
                    {TEAM_LABEL[team].label} にメンバーを追加
                  </button>

                  {/* タスクに出てくるのに登録されていない人は、まとめて入れられるようにする */}
                  {missing.length === 0 ? null : (
                    <button
                      type="button"
                      onClick={() => {
                        // 同じ一覧を見たまま同時に足すので、並び順はここで連番にする
                        const base = settings.length + 1;
                        for (const [index, name] of missing.entries()) {
                          void add({ team, name, order: base + index });
                        }
                      }}
                      className="cursor-pointer rounded bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-[var(--color-bg-surface)]"
                    >
                      タスクの担当者 {missing.length} 人を取り込む
                    </button>
                  )}
                </div>
              </section>
            );
          })}

          <p className="mt-0 mb-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
            件数はいま表示しているタスクから数えています。
          </p>
        </div>
      )}
    </main>
  );
}
